import { ref, computed } from 'vue'
import { supabase } from '../config/supabase'
import type { User, Session } from '@supabase/supabase-js'

const user = ref<User | null>(null)
const session = ref<Session | null>(null)
const loading = ref(true)
const initialized = ref(false)
let lastVisibilityCheck = 0

// Check if URL contains OAuth callback tokens
const hasOAuthCallback = () => {
  const hash = window.location.hash
  return hash.includes('access_token=') || hash.includes('error=')
}

// Initialize auth - handles both OAuth callbacks and normal sessions
const initializeAuth = async () => {
  if (initialized.value) return

  // Set up auth state listener first (this handles OAuth callbacks)
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
    session.value = newSession
    user.value = newSession?.user ?? null
    loading.value = false
    initialized.value = true
  })

  // If not an OAuth callback, proactively get the session
  // For OAuth callbacks, let the state change listener handle it
  if (!hasOAuthCallback()) {
    const { data } = await supabase.auth.getSession()
    session.value = data.session
    user.value = data.session?.user ?? null
    loading.value = false
    initialized.value = true
  }

  return subscription
}

// Start initialization
initializeAuth()

/**
 * Refresh auth session - call this when returning from sleep/background
 * Uses rate limiting to avoid excessive refreshes
 */
export async function refreshAuthSession(): Promise<void> {
  const now = Date.now()
  // Rate limit: max once per 5 seconds
  if (now - lastVisibilityCheck < 5000) return
  lastVisibilityCheck = now

  try {
    // Get fresh session from Supabase (this also refreshes the token if needed)
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      console.error('[Auth] Session refresh error:', error)
      // If session is invalid, clear state
      if (error.message?.includes('invalid') || error.message?.includes('expired')) {
        session.value = null
        user.value = null
      }
      return
    }

    // Update state with fresh session
    session.value = data.session
    user.value = data.session?.user ?? null
  } catch (e) {
    console.error('[Auth] Session refresh failed:', e)
  }
}

// Set up visibility change listener for session refresh on wake
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && initialized.value) {
      refreshAuthSession()
    }
  })
}

// Helper to wait for auth to be initialized (for router guards)
export function waitForAuthInit(): Promise<void> {
  return new Promise((resolve) => {
    if (initialized.value) {
      resolve()
      return
    }

    // Poll for initialization (auth state change will set initialized.value)
    const checkInit = setInterval(() => {
      if (initialized.value) {
        clearInterval(checkInit)
        resolve()
      }
    }, 50)

    // Timeout fallback
    setTimeout(() => {
      clearInterval(checkInit)
      resolve()
    }, 5000)
  })
}

// Export refs for shared state with useSupabaseAuth
export const authSession = session
export const authUser = user
export const authLoading = loading

export function useAuth() {
  const isSignedIn = computed(() => !!session.value)

  async function signInWithOAuth(provider: 'google' | 'apple') {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    })

    if (error) throw error
  }

  async function signInWithEmail(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) throw error
  }

  async function signUpWithEmail(email: string, password: string) {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    })

    if (error) throw error
  }

  async function signOut() {
    const { data: { session: currentSession } } = await supabase.auth.getSession()
    if (!currentSession) {
      user.value = null
      session.value = null
      return
    }

    const { error } = await supabase.auth.signOut()
    if (error) {
      if (error.name === 'AuthSessionMissingError') {
        user.value = null
        session.value = null
        return
      }
      throw error
    }
  }

  async function getToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  }

  return {
    user: computed(() => user.value),
    session: computed(() => session.value),
    isSignedIn,
    loading: computed(() => loading.value),
    signInWithOAuth,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    getToken,
  }
}
